/**
 * Customer API — calls Next.js API routes that proxy to PharmacyWire.
 * Auth token is the PW user token stored in localStorage.
 */

import { csrfHeader } from "./csrf-client";

const BASE = "/api";

function emitTokenExpired() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("bp-token-expired"));
  }
}

function checkTokenExpired(res: Response, body: { code?: string } | null) {
  if (res.status === 401 && body?.code === "token_expired") {
    emitTokenExpired();
  }
}

export type PWAddress = {
  first_name?: string;
  last_name?: string;
  address_1?: string;
  address_2?: string;
  city?: string;
  state?: string;
  country?: string;
  postcode?: string;
  phone?: string;
};

export type CustomerDemographics = {
  sex: string;
  date_of_birth: string;
  weight: string;
  weight_unit: string;
  height_feet: string;
  height_inches: string;
  smoker: string;
  pregnant: string;
  nursing: string;
  child_resistant_packaging: string;
  contact_for_refills: string;
};

export type CustomerUser = {
  id: string;
  name: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  address: PWAddress;
  demographics?: CustomerDemographics;
};

export type PWOrder = Record<string, unknown>;

export type OrdersResponse = {
  orders: PWOrder[];
  raw: string;
};

type AuthResponse = {
  token: string;
  patient_id: string;
  user: CustomerUser;
};

type ErrorBody = {
  code: string;
  message: string;
};

async function post<T>(
  path: string,
  body: Record<string, unknown>,
  token?: string,
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...csrfHeader(),
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch(`${BASE}${path}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = (await res.json().catch(() => null)) as ErrorBody | null;
      checkTokenExpired(res, err);
      return { ok: false, error: err?.message ?? `Request failed (${res.status})` };
    }

    return { ok: true, data: (await res.json()) as T };
  } catch {
    return { ok: false, error: "Network error. Please try again." };
  }
}

export type MedicalInfo = {
  gender: string | null;
  date_of_birth: string | null;
  height_value: number | null;
  height_unit: "cm" | "ft";
  weight_value: number | null;
  weight_unit: "kg" | "lbs";
  smoker: "yes" | "no" | null;
  child_resistant_packaging: "yes" | "no" | null;
  refill_notifications: "yes" | "no" | null;
};

export async function checkEmail(
  email: string,
): Promise<{ ok: true; data: { available: boolean; message: string } } | { ok: false; error: string }> {
  return { ok: true, data: { available: true, message: "" } };
}

export function loginCustomer(email: string, password: string, preferredMfaMethod?: string) {
  return post<AuthResponse>("/auth/login", {
    email,
    password,
    ...(preferredMfaMethod ? { preferred_mfa_method: preferredMfaMethod } : {}),
  });
}

export function registerCustomer(
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  phone?: string,
  address?: PWAddress,
) {
  return post<AuthResponse>("/auth/register", {
    first_name: firstName,
    last_name: lastName,
    email,
    password,
    phone,
    address,
  });
}

export function forgotPassword(email: string) {
  return post<{ success: boolean; message: string }>("/auth/forgot-password", { email });
}

export function changePassword(token: string, currentPassword: string, newPassword: string) {
  return post<{ success: boolean }>("/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  }, token);
}

export function fetchProfile(token: string) {
  return post<CustomerUser>("/account/profile", {}, token);
}

export type PatientDemographicsInput = {
  gender?: string;
  date_of_birth?: string;
  height_cm?: number;
  height_feet?: string;
  height_inches?: string;
  weight_value?: number;
  weight?: string;
  weight_unit?: string;
  smoker?: string;
  pregnant?: string;
  nursing?: string;
  pregnancy_due_date?: string;
  child_resistant_packaging?: string;
  contact_for_refills?: string;
};

export function savePatientDemographics(
  token: string,
  patientId: string,
  demographics: PatientDemographicsInput,
) {
  return patch<{ success: boolean }>(
    "/account/profile",
    { patient_id: patientId, demographics },
    token,
  );
}

export function fetchOrders(token: string, patientId: string, limit = 20) {
  return post<OrdersResponse>(
    "/account/orders",
    { patient_id: patientId, limit } as unknown as Record<string, unknown>,
    token,
  );
}

export function fetchOrderById(token: string, orderId: string) {
  return post<OrdersResponse>("/account/orders", { id: orderId } as unknown as Record<string, unknown>, token);
}

/**
 * Submit a customer-written note on an existing order. Posts to
 * PharmacyWire as an actionable comment plus a `customer-note` tag.
 */
export function addOrderNote(
  token: string,
  data: { pw_order_id: string; patient_id: string; note: string },
) {
  return post<{ success: boolean; note: string }>(
    "/account/order-note",
    data as unknown as Record<string, unknown>,
    token,
  );
}

export function placeOrder(
  token: string,
  data: {
    patient_id: string;
    items: { part_id: string; quantity: number; unit_price: string }[];
    shipping: PWAddress;
    shipping_fee?: string;
    shipping_product_id?: string;
    comments?: string;
    requires_cold_chain?: boolean;
    /**
     * Ordered list of insurers (primary first). The route surfaces
     * this as one tag per position (`insurance-primary`,
     * `insurance-secondary`, `insurance-tertiary`) plus an
     * `insurance-count` tag, and a multi-line order comment.
     */
    insurance?: { insurers: string[] };
    /**
     * Completed Global Payments 3DS2 payment from the hosted-fields
     * step: PMT_ tokenization reference + AUT_ authentication id. The
     * server adds app_id and the PW payment type.
     */
    payment?: { payment_reference: string; authentication_id: string };
  },
) {
  return post<{ success: boolean; pw_order_id: string; total: string; taxes?: TaxLineItem[]; total_tax?: string }>(
    "/checkout/place-order",
    data as unknown as Record<string, unknown>,
    token,
  );
}

export type TaxLineItem = { amount: string; description: string };

export function fetchOrderRequirements(
  token: string,
  data: {
    patient_id: string;
    items: { part_id: string; quantity: number; unit_price: string }[];
    shipping: PWAddress;
    shipping_product_id?: string;
    shipping_fee?: string;
  },
) {
  return post<{ taxes: TaxLineItem[]; total_tax: string }>(
    "/checkout/order-requirements",
    data as unknown as Record<string, unknown>,
    token,
  );
}

export type ShippingProduct = { id: string; description: string; fee: string };

export function fetchShippingProducts(
  productCodes: string[],
  subtotal: string,
  countryCode: string,
) {
  return post<{ products: ShippingProduct[] }>(
    "/checkout/shipping",
    { product_codes: productCodes, subtotal, country_code: countryCode },
  );
}

/* ── PUT / DELETE helpers ── */

async function put<T>(
  path: string,
  body: Record<string, unknown>,
  token: string,
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${BASE}${path}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => null)) as ErrorBody | null;
      checkTokenExpired(res, err);
      return { ok: false, error: err?.message ?? `Request failed (${res.status})` };
    }
    return { ok: true, data: (await res.json()) as T };
  } catch {
    return { ok: false, error: "Network error. Please try again." };
  }
}

async function patch<T>(
  path: string,
  body: Record<string, unknown>,
  token: string,
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${BASE}${path}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => null)) as ErrorBody | null;
      checkTokenExpired(res, err);
      return { ok: false, error: err?.message ?? `Request failed (${res.status})` };
    }
    return { ok: true, data: (await res.json()) as T };
  } catch {
    return { ok: false, error: "Network error. Please try again." };
  }
}

async function del<T>(
  path: string,
  body: Record<string, unknown>,
  token: string,
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${BASE}${path}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => null)) as ErrorBody | null;
      checkTokenExpired(res, err);
      return { ok: false, error: err?.message ?? `Request failed (${res.status})` };
    }
    return { ok: true, data: (await res.json()) as T };
  } catch {
    return { ok: false, error: "Network error. Please try again." };
  }
}

/* ── Global Payments hosted-fields config ── */

export type GpCheckoutConfig =
  | {
      enabled: true;
      accessToken: string;
      env: "sandbox" | "production";
      apiVersion: string;
      serviceUrl: string;
      paymentType: string;
      /** Origin serving the 3DS ACS return pages; null = same origin. */
      notificationOrigin: string | null;
    }
  | { enabled: false; reason: string };

export function fetchGpConfig(token: string) {
  return post<GpCheckoutConfig>("/checkout/gp/config", {}, token);
}

/* ── WC Payment Order Creation ── */

export type CreateWcPaymentOrderData = {
  pw_order_id: string;
  amount: string;
  billing?: Record<string, string>;
};

export async function createWcPaymentOrder(
  token: string,
  data: CreateWcPaymentOrderData,
): Promise<{ ok: true; data: { pay_url: string; wc_order_id: number } } | { ok: false; error: string }> {
  return post<{ pay_url: string; wc_order_id: number }>(
    "/checkout/create-wc-order",
    data as unknown as Record<string, unknown>,
    token,
  );
}

/* ── WC Payment Check ── */

export async function checkWcPaymentStatus(
  pwOrderIds: string[],
): Promise<{ ok: true; data: { matched: Record<string, number> } } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${BASE}/account/orders/wc-check`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pw_order_ids: pwOrderIds }),
    });

    if (!res.ok) {
      return { ok: false, error: `Request failed (${res.status})` };
    }

    return { ok: true, data: (await res.json()) as { matched: Record<string, number> } };
  } catch {
    return { ok: false, error: "Network error." };
  }
}

/* ── Shipping Addresses ── */

export type SavedShippingAddress = {
  id: string;
  description: string;
  address_1: string;
  address_2: string;
  address_3: string;
  city: string;
  state: string;
  country: string;
  postcode: string;
  phone: string;
};

export function fetchShippingAddresses(token: string, patientId: string) {
  return post<{ addresses: SavedShippingAddress[] }>(
    "/account/addresses",
    { action: "list", patient_id: patientId } as unknown as Record<string, unknown>,
    token,
  );
}

export function addShippingAddressApi(
  token: string,
  patientId: string,
  address: Omit<SavedShippingAddress, "id" | "address_3">,
) {
  return post<{ success: boolean }>(
    "/account/addresses",
    { patient_id: patientId, address } as unknown as Record<string, unknown>,
    token,
  );
}

export function editShippingAddressApi(
  token: string,
  patientId: string,
  addressId: string,
  address: Omit<SavedShippingAddress, "id" | "address_3">,
) {
  return put<{ success: boolean }>(
    "/account/addresses",
    { patient_id: patientId, address_id: addressId, address } as unknown as Record<string, unknown>,
    token,
  );
}

export function deleteShippingAddressApi(token: string, patientId: string, addressId: string) {
  return del<{ success: boolean }>("/account/addresses", { patient_id: patientId, address_id: addressId }, token);
}

/* ── Profile Update ── */

export function updateProfile(
  token: string,
  patientId: string,
  data: { phone?: string; first_name?: string; last_name?: string; email?: string; address_1?: string; address_2?: string; city?: string; state?: string; country?: string; postcode?: string; demographics?: PatientDemographicsInput },
) {
  return put<{ success: boolean }>("/account/profile", { patient_id: patientId, ...data }, token);
}

/* ── MFA ── */

export type MfaDeviceType = "totp" | "webauthn" | "sms" | "email" | "static";

export type MfaStatusResponse = {
  mfa_enabled: boolean;
  methods: MfaDeviceType[];
  authentik_user_id: number | null;
  default_method: MfaDeviceType | null;
};

export type MfaDeviceSummary = {
  type: MfaDeviceType;
  pk: number;
  name: string;
  confirmed: boolean;
  created_on?: string;
};

export type MfaEnrollResponse = {
  type: MfaDeviceType;
  device_pk?: number;
  config_url?: string;
  phone_number?: string;
  masked_email?: string;
  name?: string;
  backup_codes?: string[];
  authentik_user_id?: number;
  message?: string;
  user_token_id?: string;
  user_token_key?: string;
  registration?: Record<string, unknown>;
};

export type MfaVerifyResponse = {
  token: string;
  patient_id: string;
  user: CustomerUser;
};

export function getMfaStatus(token: string) {
  return post<MfaStatusResponse>("/auth/mfa/status", {}, token);
}

export function getMfaDevices(token: string) {
  return post<{ devices: MfaDeviceSummary[] }>(
    "/auth/mfa/devices",
    { action: "list" } as unknown as Record<string, unknown>,
    token,
  );
}

export function enrollMfa(
  token: string,
  type: MfaDeviceType,
  options?: { phone_number?: string; device_name?: string },
) {
  return post<MfaEnrollResponse>("/auth/mfa/enroll", { type, ...options }, token);
}

export function confirmMfaEnroll(
  token: string,
  type: MfaDeviceType,
  code: string,
  opts?: {
    devicePk?: number;
    user_token_id?: string;
    user_token_key?: string;
    webauthn_response?: Record<string, unknown>;
    authentik_user_id?: number;
    device_name?: string;
  },
) {
  return post<{ success: boolean; confirmed: boolean }>(
    "/auth/mfa/enroll/confirm",
    {
      type,
      code,
      device_pk: opts?.devicePk,
      user_token_id: opts?.user_token_id,
      user_token_key: opts?.user_token_key,
      webauthn_response: opts?.webauthn_response,
      authentik_user_id: opts?.authentik_user_id,
      device_name: opts?.device_name,
    },
    token,
  );
}

export function deleteMfaDevice(
  token: string,
  deviceType: MfaDeviceType,
  devicePk: number,
) {
  return del<{ success: boolean }>(
    "/auth/mfa/devices",
    { device_type: deviceType, device_pk: devicePk },
    token,
  );
}

export function verifyMfa(
  sessionId: string,
  code: string,
  method: MfaDeviceType,
  webauthnResponse?: Record<string, unknown>,
) {
  return post<MfaVerifyResponse>("/auth/mfa/verify", {
    mfa_session_id: sessionId,
    code: code || undefined,
    method,
    webauthn_response: webauthnResponse,
  });
}

export function getWebAuthnChallenge(sessionId: string) {
  return post<{ options: Record<string, unknown> }>("/auth/mfa/webauthn-challenge", {
    mfa_session_id: sessionId,
  });
}

export function sendMfaCode(
  sessionId: string,
  method: "sms" | "email",
) {
  return post<{ success: boolean }>("/auth/mfa/send-code", {
    mfa_session_id: sessionId,
    method,
  });
}

export function getDefaultMfaMethod(token: string) {
  return post<{ default_method: MfaDeviceType | null; methods: MfaDeviceType[] }>(
    "/auth/mfa/default-method",
    { action: "fetch" } as unknown as Record<string, unknown>,
    token,
  );
}

export function setDefaultMfaMethod(token: string, method: MfaDeviceType | null) {
  return put<{ success: boolean; default_method: MfaDeviceType | null }>(
    "/auth/mfa/default-method",
    { method },
    token,
  );
}

export function logMfaAudit(
  token: string,
  data: {
    action: "enrolled" | "removed" | "regenerated";
    device_type: MfaDeviceType;
    device_name?: string;
  },
) {
  return post<{ success: boolean }>(
    "/auth/mfa/audit",
    { ...data, user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "" },
    token,
  );
}

/* ── Group Members ── */

export type GroupMember = {
  patient_id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  sex: string;
  species: string;
  phone: string;
  weight: string;
  weight_unit: string;
  height_feet: string;
  height_inches: string;
  smoker: string;
  pregnant: string;
  nursing: string;
  child_resistant_packaging: string;
  contact_for_refills: string;
};

export type GroupMemberInput = {
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  sex?: string;
  species?: string;
  phone?: string;
  weight?: string;
  weight_unit?: string;
  height_feet?: string;
  height_inches?: string;
  smoker?: string;
  pregnant?: string;
  nursing?: string;
  pregnancy_due_date?: string;
  child_resistant_packaging?: string;
  contact_for_refills?: string;
};

export function fetchGroupMembers(token: string) {
  return post<{ members: GroupMember[] }>(
    "/account/members",
    { action: "list" } as unknown as Record<string, unknown>,
    token,
  );
}

export function addGroupMember(token: string, data: GroupMemberInput) {
  return post<{ success: boolean; patient_id: string }>(
    "/account/members",
    data as unknown as Record<string, unknown>,
    token,
  );
}

export function updateGroupMember(
  token: string,
  memberId: string,
  data: Partial<GroupMemberInput>,
) {
  return put<{ success: boolean }>(
    "/account/members",
    { member_id: memberId, ...data } as unknown as Record<string, unknown>,
    token,
  );
}

/* ── Prescriptions ── */

export type PrescriptionPackage = {
  id: string;
  package_id: string;
};

export type PrescriptionDrug = {
  din: string;
  drug_id: string;
  name: string;
  strength: string;
  form: string;
  generic: boolean;
  ingredient_hash: string;
  ingredient_names: string;
  manufacturer: string;
  packages: PrescriptionPackage[];
};

export type PrescriptionFill = {
  type: string;
  frequency: string;
  dispensed: string;
  remaining: string;
  expiry: string;
};

export type Prescription = {
  id: string;
  rx_number: string;
  is_ready_to_fill: boolean;
  drug: PrescriptionDrug;
  fill: PrescriptionFill;
  next_fill_date: string;
  instructions: string;
};

export function fetchPrescriptions(token: string, patientId: string) {
  return post<{ prescriptions: Prescription[] }>(
    "/account/prescriptions",
    { patient_id: patientId } as unknown as Record<string, unknown>,
    token,
  );
}

/* ── Prescription Transfer ── */

export type RxTransfer = {
  id: string;
  patient_id: string;
  pharmacy_from_name: string;
  pharmacy_from_address: string;
  pharmacy_from_phone: string;
  pharmacy_to_name: string;
  pharmacy_to_address: string;
  pharmacy_to_phone: string;
  direction: "in" | "out";
  rx_scope: "all" | "specific";
  rx_details: string;
  status: string;
  created_at: string;
};

export type RxTransferPayload = {
  pharmacy_from_name: string;
  pharmacy_from_address?: string;
  pharmacy_from_phone?: string;
  pharmacy_from_place_id?: string;
  pharmacy_to_name: string;
  pharmacy_to_address?: string;
  pharmacy_to_phone?: string;
  pharmacy_to_place_id?: string;
  direction: "in" | "out";
  rx_scope: "all" | "specific";
  rx_details?: string;
};

export function fetchRxTransfers(token: string) {
  return post<{ transfers: RxTransfer[] }>(
    "/account/rx-transfer",
    { action: "list" } as unknown as Record<string, unknown>,
    token,
  );
}

export function submitRxTransfer(token: string, payload: RxTransferPayload) {
  return post<{ transfer: RxTransfer }>("/account/rx-transfer", payload as unknown as Record<string, unknown>, token);
}

/* ── Medical Questionnaire ── */

export type MedicalQuestionOption = {
  label: string;
  value: string;
};

export type MedicalQuestion = {
  id: string;
  label: string;
  question: string;
  response: string;
  comment: string;
  sort_order: number;
  answer_options: MedicalQuestionOption[];
};

export type MedicalQuestionnaireResponse = {
  questions: MedicalQuestion[];
  show_medical_questionnaire: boolean;
};

export function fetchMedicalQuestionnaire(token: string, patientId: string) {
  return post<MedicalQuestionnaireResponse>(
    "/account/medical-questionnaire",
    { action: "fetch", patient_id: patientId } as unknown as Record<string, unknown>,
    token,
  );
}

export function submitMedicalQuestionnaire(
  token: string,
  patientId: string,
  answers: Record<string, string>,
  comments: Record<string, string>,
) {
  return post<{ success: boolean }>(
    "/account/medical-questionnaire",
    { patient_id: patientId, answers, comments },
    token,
  );
}
